import { useCallback, useEffect, useState } from "react"
import { useAuth } from "@/features/auth/context/AuthContext"
import type {
  MeeshoCombinedReport,
  PersistedMonthlyReport
} from "@/features/profit-calculators/calculator-shared/types"
import {
  getMonthlyReport,
  listMonthlyReports,
  saveMonthlyReport
} from "@/features/profit-calculators/calculator-shared/services/monthlyReportsService"
import type { ReportSourceFile } from "@/features/profit-calculators/calculator-shared/services/reportFileArchive"

const useMonthlyReports = (platform: "meesho" | "amazon", year = new Date().getFullYear()) => {
  const { user } = useAuth()
  const uid = user?.uid

  const [reports, setReports] = useState<PersistedMonthlyReport[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const refetch = useCallback(async () => {
    if (!uid) {
      setReports([])
      setLoading(false)
      return
    }
    setLoading(true)
    setError(null)
    try {
      const data = await listMonthlyReports(uid, platform, year)
      setReports(data)
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load monthly reports.")
    } finally {
      setLoading(false)
    }
  }, [uid, platform, year])

  useEffect(() => {
    refetch()
  }, [refetch])

  const getReport = useCallback(
    (monthKey: string) => {
      if (!uid) return Promise.resolve(null)
      return getMonthlyReport(uid, platform, monthKey)
    },
    [uid, platform]
  )

  const reportExists = useCallback((monthKey: string) => reports.some(r => r.monthKey === monthKey), [reports])

  const saveReport = useCallback(
    async (monthKey: string, report: MeeshoCombinedReport, sourceFiles: ReportSourceFile[]) => {
      if (!uid) throw new Error("You must be signed in to save a report.")
      const saved = await saveMonthlyReport(uid, platform, monthKey, report, sourceFiles)
      await refetch()
      return saved
    },
    [uid, platform, refetch]
  )

  return {
    reports,
    loading,
    error,
    refetch,
    getReport,
    reportExists,
    saveReport
  }
}

export { useMonthlyReports }
